// =============================================================================
// CAPABILITY MILESTONE SERVICE
// =============================================================================
//
// Genereert mijlpalen op basis van capability assessment beslissingen.
// Feitelijk, taakgericht, zonder coachtaal.
//
// =============================================================================

import {
  CapabilityDecision,
  CapabilityDomain,
  GeneratedMilestone,
  Resolution,
  CAPABILITY_DOMAIN_LABELS,
} from './types';
import { needsMilestone } from './capabilityDecision.service';

// -----------------------------------------------------------------------------
// MILESTONE GENERATION
// -----------------------------------------------------------------------------

/**
 * Generate milestone title based on decision
 * Feitelijk, zonder coachtaal
 */
function generateMilestoneTitle(decision: CapabilityDecision): string {
  const domainLabel = CAPABILITY_DOMAIN_LABELS[decision.domain];

  // Responsibility = other → delegatie mijlpaal
  if (decision.responsibility === 'other') {
    return `${domainLabel} organiseren`;
  }

  // Responsibility = self + resolution
  if (decision.responsibility === 'self' && decision.resolution) {
    switch (decision.resolution) {
      case 'learn':
        return `${domainLabel} ontwikkelen`;
      case 'outsource':
        return `${domainLabel} uitbesteden`;
      case 'hire':
        return `${domainLabel} teamlid aantrekken`;
    }
  }

  // Fallback (should not reach here if validation is correct)
  return `${domainLabel}`;
}

/**
 * Generate milestone from a capability decision
 */
export function generateMilestone(decision: CapabilityDecision): GeneratedMilestone | null {
  if (!needsMilestone(decision)) {
    return null;
  }

  const title = generateMilestoneTitle(decision);
  
  // Determine resolution type for tracking
  let resolution: GeneratedMilestone['resolution'];
  if (decision.responsibility === 'other') {
    resolution = 'delegate';
  } else if (decision.resolution) {
    resolution = decision.resolution;
  } else {
    return null; // Should not happen if validation passed
  }

  return {
    title,
    domain: decision.domain,
    resolution,
    // No globalPeriod - user can set this manually on milestone
  };
}

/**
 * Generate all milestones from assessment decisions
 */
export function generateMilestones(decisions: CapabilityDecision[]): GeneratedMilestone[] {
  return decisions
    .map(generateMilestone)
    .filter((m): m is GeneratedMilestone => m !== null);
}

// -----------------------------------------------------------------------------
// MILESTONE PREVIEW HELPERS
// -----------------------------------------------------------------------------

/**
 * Get summary of what will be created
 */
export function getMilestoneSummary(decisions: CapabilityDecision[]): {
  total: number;
  byResolution: Record<string, number>;
} {
  const milestones = generateMilestones(decisions);
  
  const byResolution: Record<string, number> = {
    learn: 0,
    outsource: 0,
    hire: 0,
    delegate: 0,
  };

  milestones.forEach(m => {
    byResolution[m.resolution]++;
  });

  return {
    total: milestones.length,
    byResolution,
  };
}
